import compact from "lodash/compact";
import flatten from "lodash/flatten";
import minBy from "lodash/minBy";
import times from "lodash/times";
import { useWindowSize } from "PFCore/helpers/use_window_size";
import { PropsWithChildren, ReactElement, ReactNode, useEffect, useRef, useState } from "react";

import css from "./freemasonry_layout.module.scss";

type FreemasonryItemProps = PropsWithChildren<{
  onMount: (child: ReactNode, height: number) => void;
}>;

const FreemasonryItem = ({ children, onMount }: FreemasonryItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current && onMount(children, ref.current.clientHeight);
  }, [ref.current]);

  return (
    <div ref={ref} className={css.item}>
      {children}
    </div>
  );
};

type FreemasonryLayoutProps = PropsWithChildren<{
  minColumnWidth?: number;
}>;

export const FreemasonryLayout = ({ children, minColumnWidth = 300 }: FreemasonryLayoutProps) => {
  const [itemsToHeight, setItemsToHeight] = useState<Record<string, number>>({});
  const [columnsCount, setColumnsCount] = useState(1);
  const [firstRender, setFirstRender] = useState(true);

  const { windowWidth } = useWindowSize();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (rootRef.current) {
      const viewportWidth = rootRef.current.clientWidth;
      setColumnsCount(Math.floor(viewportWidth / minColumnWidth) || 1);
      setFirstRender(false);
    }
  }, [windowWidth, minColumnWidth]);

  const handleItemMount = (item: ReactElement, height: number) =>
    setItemsToHeight((itemsToHeight) => ({ ...itemsToHeight, [item.key as string]: height }));

  const columns = times(columnsCount, () => ({ height: 0, items: [] as ReactElement[] }));
  // flatten and compact children so we are a bit more flexible when passing them
  const childrenArray = Array.isArray(children) ? children : [children];
  compact(flatten(childrenArray)).forEach((child: ReactElement) => {
    const col = minBy(columns, (column) => column.height || 0); // get the shortest column
    col!.height += itemsToHeight[child.key as string] || 0;
    col!.items.push(child);
  });

  return (
    <div className={css.root} ref={rootRef} style={{ opacity: firstRender ? 0 : 1 }}>
      {columns.map((col, i) => (
        <div key={i} style={{ width: `${100 / columnsCount}%` }}>
          {(col.items || []).map((item) => (
            <FreemasonryItem key={item.key} onMount={handleItemMount}>
              {item}
            </FreemasonryItem>
          ))}
        </div>
      ))}
    </div>
  );
};
